import { CheckCircle, Loader2, X } from "lucide-react"

/**
 * Stepper used by the Create Position card to surface the "approve ->
 * approve -> mint" sequence in-place instead of relying solely on toast
 * notifications. Mirrors the `SwapProcessView` house pattern: numbered
 * rows, `pending | active | done | failed` statuses, light-level
 * backgrounds, and optional per-step error banner.
 *
 * Intentionally pure — all state lives in `CreatePositionStore`, which
 * subscribes to the interaction's typed emitter events and translates
 * them into the `steps` array this component consumes. The composite is
 * the only place that knows about the store.
 */
export interface CreatePositionProcessStep {
  id: string
  title: string
  status: "pending" | "active" | "done" | "failed"
}

export interface CreatePositionProcessViewProps {
  steps: Array<CreatePositionProcessStep>
  errorMessage?: string
  onCancel?: () => void
  onCheckStatus?: () => void
  isCheckingStatus?: boolean
}

function StepRow({
  index,
  title,
  status,
}: {
  index: number
  title: string
  status: CreatePositionProcessStep["status"]
}) {
  let icon = (
    <span className="text-xs font-semibold text-t300">{index + 1}</span>
  )
  let rowClass = "bg-p400 border-p400-light"
  let titleClass = "text-t200"
  let hint: string | undefined

  if (status === "active") {
    icon = <Loader2 className="h-4 w-4 animate-spin text-p100" />
    rowClass = "bg-p300 border-p100 shadow-[0_0_15px_rgba(59,130,246,0.08)]"
    titleClass = "text-t100"
    hint = "Confirm in your wallet…"
  } else if (status === "done") {
    icon = <CheckCircle className="h-4 w-4 fill-green-400/20 text-green-400" />
    rowClass = "bg-p300 border-p300-light"
    titleClass = "text-t100"
  } else if (status === "failed") {
    icon = <X className="h-4 w-4 text-red-400" />
    rowClass = "bg-p400 border-red-500/30"
    titleClass = "text-t100"
  }

  return (
    <div
      className={`flex items-center gap-3 rounded-xl border p-3 transition-all duration-300 ${rowClass}`}
      data-testid={`create-position-step-${index + 1}`}
      data-status={status}
    >
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-p400">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${titleClass}`}>{title}</div>
        {hint ? <div className="mt-0.5 text-xs text-t300">{hint}</div> : null}
      </div>
    </div>
  )
}

export function CreatePositionProcessView({
  steps,
  errorMessage,
  onCancel,
  onCheckStatus,
  isCheckingStatus = false,
}: CreatePositionProcessViewProps) {
  const hasFailure = steps.some((step) => step.status === "failed")
  const allDone = steps.length > 0 && steps.every((step) => step.status === "done")
  const hasActiveStep = steps.some((step) => step.status === "active")
  const canCheckStatus = !!onCheckStatus && !allDone && (hasFailure || hasActiveStep)

  return (
    <div
      className="space-y-2 rounded-2xl border border-p400-light bg-p400 p-3"
      role="status"
      aria-live="polite"
      data-testid="create-position-process-view"
    >
      <div className="flex items-center justify-between px-1">
        <div className="text-xs font-semibold uppercase tracking-wider text-t300">
          {allDone ? "Position created" : hasFailure ? "Transaction failed" : "Creating position"}
        </div>
        <div className="flex items-center gap-1">
          {canCheckStatus ? (
            <button
              type="button"
              onClick={onCheckStatus}
              disabled={isCheckingStatus}
              className="rounded-md px-2 py-1 text-xs font-semibold text-t200 transition-colors hover:bg-p300 hover:text-t100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCheckingStatus ? "Checking…" : "Check status"}
            </button>
          ) : null}
          {onCancel && hasFailure ? (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-2 py-1 text-xs font-semibold text-t200 transition-colors hover:bg-p300 hover:text-t100"
            >
              Reset
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        {steps.map((step, index) => (
          <StepRow key={step.id} index={index} title={step.title} status={step.status} />
        ))}
      </div>

      {hasFailure && errorMessage ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-400">
          {errorMessage}
        </div>
      ) : null}
    </div>
  )
}
